import React, { Children, isValidElement, ReactElement, ReactNode } from "react";
import * as Tabs from "@radix-ui/react-tabs";

interface ResponsiveTabsListProps {
    value: string;
    onValueChange: (value: string) => void;
    children: ReactNode;
    secondaryChildren?: ReactNode;
    secondaryLabel?: string;
    renderSecondaryRow?: boolean;
    className?: string;
    secondaryClassName?: string;
    selectClassName?: string;
    selectAriaLabel?: string;
}

const DEFAULT_LIST_CLASS_NAME = "responsive-tabs-list";
const DEFAULT_SELECT_CLASS_NAME =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

const extractText = (node: ReactNode): string => {
    if (node === null || typeof node === "undefined" || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (isValidElement(node)) return extractText((node.props as { children?: ReactNode }).children);
    return "";
};

const collectOptions = (children: ReactNode): { value: string; label: string }[] => {
    const options: { value: string; label: string }[] = [];
    Children.forEach(children, (child) => {
        if (!isValidElement(child)) return;
        const props = child.props as { value?: unknown; children?: ReactNode };
        if (typeof props.value === "string") {
            const text = extractText(props.children);
            options.push({ value: props.value, label: text || props.value });
        }
    });
    return options;
};

export const ResponsiveTabsList = ({
    value,
    onValueChange,
    children,
    secondaryChildren,
    secondaryLabel,
    renderSecondaryRow = true,
    className,
    secondaryClassName,
    selectClassName,
    selectAriaLabel = "Select tab"
}: ResponsiveTabsListProps): ReactElement => {
    const selectId = React.useId();
    const primaryOptions = collectOptions(children);
    const secondaryOptions = secondaryChildren ? collectOptions(secondaryChildren) : [];

    let listClassName = DEFAULT_LIST_CLASS_NAME;
    if (className) {
        listClassName = DEFAULT_LIST_CLASS_NAME + " " + className;
    }

    let secondaryListClassName = DEFAULT_LIST_CLASS_NAME + " responsive-tabs-list-secondary";
    if (secondaryClassName) {
        secondaryListClassName = secondaryListClassName + " " + secondaryClassName;
    }

    return (
        <>
            <div className="responsive-tabs-select">
                <label htmlFor={selectId} className="responsive-tabs-select-label">
                    {selectAriaLabel}
                </label>
                <select
                    id={selectId}
                    value={value}
                    onChange={(e) => onValueChange(e.target.value)}
                    className={selectClassName ?? DEFAULT_SELECT_CLASS_NAME}
                >
                    {primaryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                    {secondaryOptions.length > 0 && secondaryLabel ? (
                        <optgroup label={secondaryLabel}>
                            {secondaryOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </optgroup>
                    ) : (
                        secondaryOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))
                    )}
                </select>
            </div>
            {secondaryChildren && renderSecondaryRow ? (
                <div className="responsive-tabs-lists">
                    <Tabs.List className={listClassName}>{children}</Tabs.List>
                    <div className="responsive-tabs-secondary">
                        {secondaryLabel ? <span className="responsive-tabs-secondary-label">{secondaryLabel}</span> : null}
                        <Tabs.List className={secondaryListClassName}>{secondaryChildren}</Tabs.List>
                    </div>
                </div>
            ) : (
                <Tabs.List className={listClassName}>{children}</Tabs.List>
            )}
        </>
    );
};
